import React from 'react';
import { Select } from 'antd';
import { useQuery } from '@apollo/client';
import { GET_ALL_CHANNEL_LITE, allChannelLiteResponse } from '../../graphql/queries/getChannelAll';
import { useFilterLabelClassName, useFilterContainerClassName, useFilterInputContainerClassName } from '../../utils/languageUtils';

interface ChannelSelectFilterBaseProps {
  label: string;
  spanClassName?: string;
  placeholder?: string;
  className?: string;
}

interface ChannelSelectFilterSingleProps extends ChannelSelectFilterBaseProps {
  multiple?: false;
  value?: string;
  onChange: (value: string | undefined) => void;
  onSelect?: (value: string, option: { label: string; value: string }) => void;
  required?: boolean;
  disabled?: boolean;
  /**
   * 强制标签在上、输入框在下（block 布局），不跟随语言自适应的左/上布局。
   * 财务统计等页面自己的筛选项是手写 `<label className="block ...">` 固定纵向堆叠的，
   * 传 true 让 ChannelSelectFilter 跟这些页面自己的同排筛选项保持一致。
   */
  stacked?: boolean;
}

interface ChannelSelectFilterMultipleProps extends ChannelSelectFilterBaseProps {
  multiple: true;
  value?: string[];
  onChange: (value: string[] | undefined) => void;
}

type ChannelSelectFilterProps = ChannelSelectFilterSingleProps | ChannelSelectFilterMultipleProps;

/**
 * 渠道下拉（全量渠道，id/channelName），数据源见 getAllChannelsLite。
 * 通过 `multiple` 属性切换单选/多选，与全局共享的 ClientSelectFilter/MultiClientSelectFilter
 * 数据源相同，各自维护，互不影响。默认跟 InputFilterItem/ParcelFilterStyle/DateRangeFilterItem
 * 等同排筛选项一样用 useFilterContainerClassName 等共享 hook（语言自适应：中文标签在左，外语
 * 标签在上）；单选场景如果同排筛选项是财务统计页面那种手写 `<label className="block ...">`
 * 固定纵向堆叠风格，传 `stacked` 让标签固定在上，不跟语言联动。
 */
export const ChannelSelectFilter: React.FC<ChannelSelectFilterProps> = (props) => {
  const { label, spanClassName, placeholder = 'All', className } = props;
  const { data, loading } = useQuery<allChannelLiteResponse>(GET_ALL_CHANNEL_LITE);

  const options = React.useMemo(() => {
    return (data?.getAllChannelsLite || []).map(ch => ({
      label: ch.channelName,
      value: ch.id,
      searchText: ch.channelName
    }));
  }, [data]);

  // 两个 hook 调用必须在条件分支之前无条件执行（Rules of Hooks），即使单选分支不用它们的返回值
  const containerClassName = useFilterContainerClassName(className);
  const labelClassName = useFilterLabelClassName(spanClassName);
  const inputContainerClassName = useFilterInputContainerClassName();

  if (props.multiple) {
    const { value, onChange } = props;

    return (
      <div className={containerClassName}>
        <span className={labelClassName}>{label}</span>
        <div className={inputContainerClassName}>
          <Select
            mode="multiple"
            showSearch
            allowClear
            placeholder={placeholder}
            value={value}
            onChange={(val) => onChange(val && val.length > 0 ? val : undefined)}
            loading={loading}
            options={options}
            filterOption={(input, option) =>
              (option?.searchText ?? '').toLowerCase().includes(input.toLowerCase())
            }
            className="w-full"
            size="middle"
            maxTagCount="responsive"
            maxTagTextLength={10}
            tagRender={(tagProps) => {
              const { label: tagLabel, closable, onClose } = tagProps;
              return (
                <span className="ant-select-selection-item inline-flex items-center bg-gray-100 text-gray-800 rounded px-2 py-1 text-sm mr-1 mb-1">
                  <span className="ant-select-selection-item-content truncate max-w-[120px]">{tagLabel}</span>
                  {closable && (
                    <span
                      className="ant-select-selection-item-remove ml-1 cursor-pointer hover:text-red-500"
                      onClick={onClose}
                    >
                      ×
                    </span>
                  )}
                </span>
              );
            }}
          />
        </div>
      </div>
    );
  }

  const { value, onChange, onSelect, required = false, disabled = false, stacked = false } = props;

  const select = (
    <Select
      showSearch
      allowClear
      placeholder={placeholder}
      value={value}
      onChange={(val) => onChange((val as string) || undefined)}
      onSelect={(val, option) => {
        if (onSelect && option) {
          onSelect(val as string, option as { label: string; value: string });
        }
      }}
      loading={loading}
      options={options}
      filterOption={(input, option) =>
        (option?.searchText ?? '').toLowerCase().includes(input.toLowerCase())
      }
      className="w-full"
      disabled={disabled}
    />
  );

  if (stacked) {
    // 跟财务统计页面里手写的 <label className="block text-sm font-medium mb-1"> 保持一致，
    // 不用 useFilterLabelClassName（那套是给 flex 布局用的，跟这里的纵向堆叠不是一回事）
    return (
      <div className={className}>
        <label className={`block text-sm font-medium mb-1 ${spanClassName || ''}`}>
          {required && <span style={{ color: '#ff4d4f', marginRight: '4px' }}>*</span>}
          {label}
        </label>
        {select}
      </div>
    );
  }

  return (
    <div className={containerClassName}>
      <span className={labelClassName}>
        {required && <span style={{ color: '#ff4d4f', marginRight: '4px' }}>*</span>}
        {label}
      </span>
      <div className={inputContainerClassName}>
        {select}
      </div>
    </div>
  );
};

export default ChannelSelectFilter;
